// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// React
import { forwardRef, useState, useEffect, useRef, useContext } from 'react';

// Material UI
import {
  Box,
  Card,
  Stack,
} from "@mui/material";

// Icons
import CodeIcon from '@mui/icons-material/Code';
import CodeOffIcon from '@mui/icons-material/CodeOff';

// Contexts
import { UserContext } from "../../Contexts";

// Custom Components
import { AligningBox } from '../utils/AligningBox.js';
import IconToggleButton from '../utils/IconToggleButton.js';
import LoadingIcon from "../loading/LoadingIcon.js";
import RecordingIndicator from '../utils/RecordingIndicator.js';
import RenderIf from '../utils/RenderIf.js';
import { VideoControlButtons, VideoControlPlaybackBar, VideoControlCountdownButton } from "./VideoControls.js";

// Custom Scripts
import { LoadedDataState } from "../../scripts/loadedData/loadedData";
import { useFullscreenElement } from '../../scripts/fullscreen.js';
import { constructCloudFrontURL } from '../../scripts/utils.js';


// ----------------------------------------------------------------------------
//   Setup
// ----------------------------------------------------------------------------


const ALLOW_VIDEO_CONTROLS = false
const SHOW_VIDEO_STATE     = false
const DEV_CONTROL_OVERRIDES = false



// ----------------------------------------------------------------------------
//   Video Component
// ----------------------------------------------------------------------------


export const CustomVideo = forwardRef(({ videoURL, ccURL, useCaptions, thumbnailTime = 0, usePoster = true }, ref) => {

  // TODO: Better file organization
  const cleanPath = videoURL.split('cloudfront.net/').at(-1);
  const thumbParts = cleanPath.split('/');
  const thumbURL = `${ thumbParts.slice(0, thumbParts.length - 1).join('/') }/thumb/${ thumbParts.at(-1).replace('.mp4', '') }/title.jpg`;

  // If a captions URL is provided, create a track object
  const [ $ccTrack, setCCTrack ] = useState(null)
  useEffect(() => {
    if ( ccURL ) {
      setCCTrack(
        <track
          label   = "English"
          kind    = "subtitles"
          srcLang = "en"
          src     =  { ccURL }
          default
        />
      )
    }
  }, [ ccURL ])

  return (
    <Box
      gap={0} backgroundColor="black" width="100%" height="100%"
      display="flex" flexDirection="column" justifyContent="center"
      position="absolute"
    >
      <RenderIf condition={videoURL} fallback={<Box width="100%" height="100%" backgroundColor="black" />}>
        <video
          ref={ ref }
          poster={ usePoster ? constructCloudFrontURL(thumbURL) : null }
          loop={false} playsInline="playsinline" preload="auto"
          style={{width: "100%", height: "100%", objectFit: "contain"}}
          crossOrigin="anonymous"
          controls={ ALLOW_VIDEO_CONTROLS }
          controlsList="nodownload nofullscreen noplaybackrate"
          disablePictureInPicture={true}
        >
          <source src={ thumbnailTime ? `${ videoURL }#t=${ thumbnailTime }` : videoURL } type="video/mp4" />
          { useCaptions ? $ccTrack : null }
        </video>
      </RenderIf>
    </Box>
  )
})



// ----------------------------------------------------------------------------
//   Video Content Card Component
// ----------------------------------------------------------------------------


export function PracticeVideoCard({
  videoContent,
  videoState,
  videoManager,
  isRecording,
  playRef,
  onCountdown,
  isComplete,
  finishButton,
  playbackOnly = false,
}) {

  // --------------------------------------------------------------------------
  //   Context & State
  // --------------------------------------------------------------------------

  const optionsBarHeight = '64px';

  // In playback-only mode (nothing is recorded), treat playing like recording for the overlays
  const hidePlayOverlay = playbackOnly ? videoState.isPlaying : isRecording;

  // Get user (to check role)
  const { userData } = useContext( UserContext );
  const allowAdminFeatures = userData?.value?.role === 'admin';

  // Reference to the element that should be fullscreened
  const [ useFullscreen, fullscreenRef, setFullscreen ] = useFullscreenElement( null )

  // Control captions
  const [ useCaptions,  setCaptions  ] = useState(false);
  const [ useCountdown, setCountdown ] = useState(true);

  // Control developer tools
  const [ showDevTools, setShowDevTools ] = useState(false);
  const devIconStyles = {
    sx: {
      color: 'white',
      opacity: 0.75,
    }
  }


  // --------------------------------------------------------------------------
  //   Callbacks
  // --------------------------------------------------------------------------

  // Handler for rewind button
  const onRewind = (sec) => {
    videoManager.moveBySeconds( -sec );
  }

  // Handler for progress bar seeking
  const onSeekPercent = (percent) => {
    videoManager.seekToPercent( percent, { holdUntilFinished: true, ignoreMax: allowAdminFeatures } );
  }

  // When seeking ends, resume the video
  const onSeekEnd = () => {
    videoManager.finishSeeking();
  }


  // --------------------------------------------------------------------------
  //   Element Layout
  // --------------------------------------------------------------------------

  return (
    <Card h="100%" sx={{position: "relative"}}>
      <RenderIf
        condition = {videoContent && videoContent.state === LoadedDataState.SUCCESS}
        fallback  = {<LoadingIcon />}
      >
        <Stack ref={ fullscreenRef } height="100%" direction="column">

          {/* Main Content */}
          <Box flexGrow={1} position="relative" minHeight="0">

            {/* Video */}
            <CustomVideo
              ref         = { videoState.setVideoRef }
              videoURL    = { videoContent?.value?.url ? constructCloudFrontURL(videoContent.value.url) : null }
              ccURL       = { videoContent?.value?.cc_url ? constructCloudFrontURL(videoContent.value.cc_url) : null }
              useCaptions = { useCaptions }
            />

            {/* Play Button Overlay */}
            <RenderIf condition={ !hidePlayOverlay && !isComplete }>
              <Box
                position="absolute" height="100%" width="100%"
                display="flex" justifyContent="center" alignItems="center"
                sx={{
                  zIndex: '99',
                  backdropFilter: 'brightness(65%) grayscale(25%)',
                }}
              >
                <RenderIf
                  condition = { !videoState.isBuffering }
                  fallback  = {<LoadingIcon />}
                >
                  <VideoControlCountdownButton
                    ref          = { playRef }
                    onStart      = { videoState.progressPercent < 100 ? videoManager.play : () => {} }
                    onCount      = { onCountdown }
                    canStart     = { !(videoState.isBuffering || videoState.isPlaying) }
                    useCountdown = { useCountdown }
                  />
                </RenderIf>
              </Box>
            </RenderIf>

            {/* Finish Button Overlay */}
            <RenderIf condition={ finishButton && isComplete }>
              <AligningBox top={0}>
                { finishButton }
              </AligningBox>
            </RenderIf>

            {/* Stats Overlay */}
            <RenderIf condition={ showDevTools }>
              <AligningBox top={0} left={0} alignX="left" alignY="top" className="noselect" sx={{color: "white"}}>
                <Box component="pre" margin="12px" marginTop={ useFullscreen ? '56px' : '12px' }>
                  Playing:   {`${videoState.isPlaying}`}
                  <br />
                  Paused:    {`${videoState.isPaused}`}
                  <br />
                  Buffering: {`${videoState.isBuffering}`}
                  <br />
                  On Hold:   {`${videoState.isOnHold}`}
                </Box>
              </AligningBox>
            </RenderIf>

            {/* Loading Overlay */}
            <RenderIf condition={ hidePlayOverlay && videoState.isBuffering }>
              <AligningBox sx={{ zIndex: '99', backdropFilter: 'brightness(65%) grayscale(25%)' }} >
                <LoadingIcon />
              </AligningBox>
            </RenderIf>

            {/* Recording Indicator (Fullscreen) */}
            <RenderIf condition={ useFullscreen && !playbackOnly }>
              <AligningBox alignX="left" alignY="top" padding="16px" className="noselect">
                <RecordingIndicator active={ isRecording } showBg={ true } />
              </AligningBox>
            </RenderIf>

            {/* Video debugging button */}
            <RenderIf condition={ allowAdminFeatures }>
              <AligningBox alignX="right" alignY="top" className="noselect">
                <Box backgroundColor="rgba(0, 0, 0, 0.5)" margin="12px" borderRadius="50%" className="yesselect">
                  <IconToggleButton
                    state={ showDevTools } onToggle={ setShowDevTools }
                    iconActive   = { <CodeIcon    {...devIconStyles} /> }
                    iconInactive = { <CodeOffIcon {...devIconStyles} /> }
                  />
                </Box>
              </AligningBox>
            </RenderIf>

          </Box>
          {/* /Main Content */}

          {/* Playback Bar */}
          <VideoControlPlaybackBar
            progress    = { videoState.progressPercent }
            maxProgress = { videoState.maxProgressPercent }
            onChange    = { onSeekPercent }
            onSeekEnd   = { onSeekEnd }
            stopAtEnd   = { !DEV_CONTROL_OVERRIDES }
            allowSkipAhead = { allowAdminFeatures }
            useMaxProgress
          />

          {/* Controls */}
          <Box height={ optionsBarHeight } width="100%" bgcolor="white">
            <VideoControlButtons
              canStart      = { true }
              isPlaying     = { videoState.isPlaying }
              canPlay       = { false }
              onStop        = { videoManager.pause }
              onRewind      = { isComplete ? null : onRewind }
              hasCaptions    useCaptions   = { useCaptions }    setCaptions   = { setCaptions }
              canFullscreen  useFullscreen = { useFullscreen }  setFullscreen = { setFullscreen }
              canCountdown   useCountdown  = { useCountdown }   setCountdown  = { setCountdown }
            />
          </Box>
          {/* /Controls */}

        </Stack>
      </RenderIf>
    </Card>
  )
}


// ----------------------------------------------------------------------------
//   Export(s)
// ----------------------------------------------------------------------------

export default {
  CustomVideo,
  PracticeVideoCard,
}
